import { useEffect, useRef } from 'react';
import { Chart } from '@antv/g2';

interface DataItem {
  date: string;
  value: number;
  category: string;
}

interface LineChartProps {
  data: DataItem[];
  width?: number;
  height?: number;
  className?: string;
  chartColor?: string;
}

export default function LineChart({
  data,
  width = 600,
  height = 300,
  className = '',
  chartColor = '#00c853',
}: LineChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // 创建图表实例
    const chart = new Chart({
      container: containerRef.current,
      autoFit: true,
      width,
      height,
      padding: 0,
      margin: 3,
    });

    // 设置数据
    chart.data(data);

        // 计算数据的最大值和最小值
    const values = data.map(item => item.value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const valueRange = maxValue - minValue;

    // 配置坐标轴
    chart.scale({
      date: {
        range: [0, 1],
      },
      value: {
        nice: true,
        min: minValue - valueRange * 0.1, // 向下扩展10%的范围
        max: maxValue + valueRange * 0.1, // 向上扩展10%的范围
      },
    });

    // 隐藏坐标轴
    chart.axis(false);

    // 隐藏图例
    chart.legend(false);

    // g2 5.0 禁用tooltip
    chart.interaction('tooltip', false);

    // 绘制折线
    chart
      .line()
      .encode('x', 'date')
      .encode('y', 'value')
      .style('lineWidth', 1)
      .style('stroke', chartColor);

    // 渲染图表
    chart.render();

    // 保存图表实例
    chartRef.current = chart;

    // 清理函数
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [data, width, height, chartColor]);

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      if (chartRef.current) {
        chartRef.current.render();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`${className}`}
    />
  );
}
